import { useEffect, useRef, useState } from "react"
import { useGetAviatorResultsQuery, useGetAviatorCurrentRoundQuery } from "../../services/aviator/aviatorApi"
import "../../pages/Aviator/AviatorGame.scss"
import aviatorImg from "../../assets/aviator.png"

const CHART_W  = 360
const CHART_H  = 220
const MARGIN_L = 44
const MARGIN_B = 24

function multToY(m, maxM) {
  const ratio = Math.max(0, Math.min(1, (m - 1) / Math.max(maxM - 1, 1)))
  return CHART_H - ratio * (CHART_H - 10)
}
function timeToX(t, maxT) {
  return Math.min((t / Math.max(maxT, 1)) * CHART_W, CHART_W)
}

const buildPath = (elapsed, maxT, maxM) => {
  if (elapsed <= 0) return ""
  const N = 50
  const coords = []
  for (let i = 0; i <= N; i++) {
    const t = (elapsed * i) / N
    const m = Math.pow(Math.E, 0.07 * t)
    coords.push({ x: MARGIN_L + timeToX(t, maxT), y: multToY(m, maxM) })
  }
  let d = `M ${coords[0].x.toFixed(1)} ${coords[0].y.toFixed(1)}`
  for (let i = 1; i < coords.length; i++) {
    const p0 = coords[Math.max(i - 2, 0)]
    const p1 = coords[i - 1]
    const p2 = coords[i]
    const p3 = coords[Math.min(i + 1, coords.length - 1)]
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`
  }
  return d
}

const AviatorDisplay = () => {
  const [phase, setPhase]       = useState("waiting")
  const [multiplier, setMult]   = useState(1.0)
  const [countdown, setCD]      = useState(5)
  const [crashAt, setCrashAt]   = useState(null)
  const [history, setHistory]   = useState([])
  const [roundId, setRoundId]   = useState("")
  const [pathD, setPathD]       = useState("")
  const [dotPos, setDotPos]     = useState({ x: MARGIN_L, y: CHART_H })
  const [maxMult, setMaxMult]   = useState(2)

  const wsRef          = useRef(null)
  const mountedRef     = useRef(true)
  const animRef        = useRef(null)
  const startRef       = useRef(0)
  const multRef        = useRef(1.0)
  const reconnectRef   = useRef(null)
  const resultTRef     = useRef(null)

  const { data: resultsData }      = useGetAviatorResultsQuery(undefined, { pollingInterval: 10000 })
  const { data: currentRoundData } = useGetAviatorCurrentRoundQuery(undefined, { pollingInterval: 3000 })

  useEffect(() => {
    const list = resultsData?.data ?? []
    if (list.length) setHistory(list.map(r => parseFloat(r.crashPoint)).slice(0, 20))
  }, [resultsData])

  // Fallback sync from current-round API when WS is not connected
  useEffect(() => {
    if (!currentRoundData) return
    const rd = currentRoundData.data ?? currentRoundData
    if (!rd) return
    if (rd.status === "betting") {
      setPhase("waiting")
      if (rd.autotime !== undefined) setCD(rd.autotime)
    } else if (rd.status === "flying" && phase !== "flying") {
      const m = parseFloat(rd.multiplier ?? 1)
      multRef.current = m; setMult(m); setPhase("flying")
      startRef.current = Date.now() - (Math.log(Math.max(m, 1.0001)) / 0.07) * 1000
      if (!animRef.current) startFlying()
    }
  }, [currentRoundData])

  const stopAnim = () => {
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null }
  }

  const updateChart = (elapsed, maxT, maxM) => {
    setPathD(buildPath(elapsed, maxT, maxM))
    const x = MARGIN_L + timeToX(elapsed, maxT)
    const y = multToY(Math.pow(Math.E, 0.07 * elapsed), maxM)
    setDotPos({ x, y })
  }

  const startFlying = () => {
    let lastChartUpdate = 0
    const loop = () => {
      if (!mountedRef.current) return
      const t   = (Date.now() - startRef.current) / 1000
      const now = Date.now()
      if (now - lastChartUpdate >= 60) {
        lastChartUpdate = now
        const m    = multRef.current
        const maxT = Math.max(t / 0.72, 8)
        const maxM = Math.max(m * 1.35, 2)
        setMaxMult(maxM)
        updateChart(t, maxT, maxM)
      }
      animRef.current = requestAnimationFrame(loop)
    }
    animRef.current = requestAnimationFrame(loop)
  }

  useEffect(() => {
    mountedRef.current = true
    const apiBase = import.meta.env.VITE_API_BASE_URL || ""
    const wsBase  = apiBase.replace(/^http(s?)/, "ws$1")
    const wsUrl   = `${wsBase}/api/aviator/ws`

    const connect = () => {
      if (!mountedRef.current) return
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data)

          if (msg.type === "init") {
            if (msg.roundId) setRoundId(msg.roundId)
            if (msg.status === "betting") {
              setPhase("waiting"); setMult(1.0); setCrashAt(null)
              setCD(msg.autotime ?? 5)
              stopAnim(); setPathD(""); setDotPos({ x: MARGIN_L, y: CHART_H })
            } else if (msg.status === "flying") {
              const m = parseFloat(msg.multiplier ?? 1)
              multRef.current = m; setMult(m); setPhase("flying")
              startRef.current = Date.now() - (Math.log(Math.max(m, 1.0001)) / 0.07) * 1000
              if (!animRef.current) startFlying()
            }
          }

          if (msg.type === "newRound") {
            if (msg.roundId) setRoundId(msg.roundId)
            setPhase("waiting"); setMult(1.0); setCrashAt(null)
            setPathD(""); setDotPos({ x: MARGIN_L, y: CHART_H })
            stopAnim()
          }

          if (msg.type === "tick") {
            if (msg.roundId) setRoundId(msg.roundId)
            if (msg.status === "betting") {
              setCD(msg.autotime ?? 0)
            } else if (msg.multiplier !== undefined) {
              const m = parseFloat(msg.multiplier)
              multRef.current = m; setMult(m)
            }
          }

          if (msg.type === "flying") {
            if (msg.roundId) setRoundId(msg.roundId)
            multRef.current = 1.0; setMult(1.0); setPhase("flying"); setCrashAt(null)
            startRef.current = Date.now()
            setPathD(""); setDotPos({ x: MARGIN_L, y: CHART_H })
            stopAnim(); startFlying()
          }

          if (msg.type === "crashed") {
            const cp = parseFloat((msg.crashPoint ?? msg.multiplier ?? 1).toFixed(2))
            multRef.current = cp; setMult(cp); setCrashAt(cp); setPhase("crashed")
            stopAnim()
            setHistory(prev => [cp, ...prev].slice(0, 20))
          }
        } catch {}
      }

      ws.onerror = () => {}
      ws.onclose = () => {
        reconnectRef.current = setTimeout(() => {
          if (mountedRef.current) connect()
        }, 3000)
      }
    }

    connect()

    return () => {
      mountedRef.current = false
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      if (resultTRef.current) clearTimeout(resultTRef.current)
      stopAnim()
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.onmessage = null
        wsRef.current.close()
      }
    }
  }, [])

  const multColor = phase === "crashed" ? "#ef4444"
    : multiplier >= 10 ? "#ec4899"
    : multiplier >= 5  ? "#f59e0b"
    : multiplier >= 2  ? "#10b981"
    : "#ffffff"

  const yLabels = (() => {
    const top  = maxMult
    const step = top <= 2 ? 0.2 : top <= 5 ? 0.5 : top <= 10 ? 1 : 2
    const labels = []
    let v = 1.0
    while (v <= top * 1.05) { labels.push(parseFloat(v.toFixed(1))); v += step }
    return labels.slice(0, 8)
  })()

  return (
    <div className="av2-container" style={{ minHeight: 'unset', paddingBottom: 0 }}>
      {/* History header */}
      <div className="av2-header" style={{ justifyContent: 'flex-end' }}>
        <div className="av2-hist">
          {history.map((v, i) => (
            <span key={i} className={`av2-hpill ${v >= 2 ? "av2-hp-g" : v >= 1.5 ? "av2-hp-y" : "av2-hp-r"}`}>
              {v.toFixed(2)}x
            </span>
          ))}
        </div>
      </div>

      {/* Game canvas */}
      <div className="av2-game">
        <div className="av2-rays" />

        {phase === "flying" && (
          <svg className="av2-svg" viewBox={`0 0 ${MARGIN_L + CHART_W + 10} ${CHART_H + MARGIN_B}`} preserveAspectRatio="none">
            <defs>
              <linearGradient id="flyFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(239,68,68,0.35)" />
                <stop offset="100%" stopColor="rgba(239,68,68,0.05)" />
              </linearGradient>
            </defs>
            {yLabels.map(v => {
              const y = multToY(v, maxMult)
              return (
                <g key={v}>
                  <line x1={MARGIN_L} y1={y} x2={MARGIN_L + CHART_W} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="0.5" />
                  <text x={MARGIN_L - 4} y={y + 3} fontSize="7" fill="rgba(255,255,255,0.35)" textAnchor="end">{v.toFixed(1)}x</text>
                </g>
              )
            })}
            {pathD && (
              <path d={`${pathD} L ${dotPos.x} ${CHART_H} L ${MARGIN_L} ${CHART_H} Z`} fill="url(#flyFill)" />
            )}
            {pathD && (
              <path d={pathD} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            )}
            {pathD && (
              <circle cx={dotPos.x} cy={dotPos.y} r="3" fill="#10b981">
                <animate attributeName="r" values="3;4.5;3" dur="1s" repeatCount="indefinite" />
              </circle>
            )}
          </svg>
        )}

        {phase === "crashed" && pathD && (
          <svg className="av2-svg av2-svg-crash" viewBox={`0 0 ${MARGIN_L + CHART_W + 10} ${CHART_H + MARGIN_B}`} preserveAspectRatio="none">
            <path d={`${pathD} L ${dotPos.x} ${CHART_H} L ${MARGIN_L} ${CHART_H} Z`} fill="rgba(239,68,68,0.18)" />
            <path d={pathD} fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx={dotPos.x} cy={dotPos.y} r="3" fill="#ef4444" />
          </svg>
        )}

        {phase === "flying" && (() => {
          const pctX = Math.min(dotPos.x / (MARGIN_L + CHART_W + 10) * 100, 88)
          const pctY = Math.max(dotPos.y / (CHART_H + MARGIN_B) * 100, 8)
          return (
            <>
              <div className="av2-exhaust" style={{ left: `${pctX}%`, top: `${pctY}%` }}>
                {[0,1,2,3,4,5].map(i => <span key={i} />)}
              </div>
              <img src={aviatorImg} alt="plane" className="av2-plane" style={{ left: `${pctX}%`, top: `${pctY}%` }} />
            </>
          )
        })()}

        {phase !== "waiting" && (
          <div className="av2-mult-wrap">
            {phase === "crashed" && <div className="av2-flew">FLEW AWAY!</div>}
            <div className="av2-mult" style={{ color: multColor }}>
              {phase === "crashed" ? crashAt?.toFixed(2) : multiplier.toFixed(2)}x
            </div>
          </div>
        )}

        {phase === "waiting" && (
          <div className="av2-cd-card">
            <div className="av2-cd-logo">✈ Aviator</div>
            <div className="av2-cd-txt">Round starting in</div>
            <div className="av2-cd-num">{countdown}s</div>
          </div>
        )}

        {roundId && <div className="av2-round-id">{roundId.slice(-8)}</div>}
      </div>
    </div>
  )
}

export default AviatorDisplay
